<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight"
    :embedded="embedded">

    <div class="row no-gutter h-100">
      <div class="col-3" v-if="showList">
        <div v-if="showDateFilter">
          <ReportFilterPopover
            headerTitle="NCR Filter"
            :showApplyButton="true"
            :showResetButton="true"
            :appendToBody="true"
            alignment="left"
            placement="bottom-end"
            @apply="$emit('dateApplied')"
            @reset="$emit('dateReset')">
            <template v-slot:popover>
              <ReportDateRangeSelect 
                :startDate="editingStartDate"
                :endDate="editingEndDate"
                @updateStartDate="editingStartDate = $event; $emit('updateEditingStartDate', $event)"
                @updateEndDate="editingEndDate = $event; $emit('updateEditingEndDate', $event)"
              />
              <ReportContactSelect
                :value="editingContactId"
                @update="editingContactId = $event; $emit('updateEditingContactId', $event)"
              />
            </template>
            <template v-slot:filterDisplay>
              <div>
                <i class="far fa-calendar mr-2"></i>{{startDate | formatDate}} - {{endDate | formatDate}}
              </div>
              <div v-if="selectedContact">
                <i class="far fa-user-circle mr-2"></i>{{selectedContact.name}}
              </div>
            </template>
          </ReportFilterPopover>
        </div>
        <Scroller :maxHeight="450">
          <StaticDataTable class="static-table-list" v-if="hasChartData" :smallRows="true">
            <template v-slot:header>
              <tr>
                <th width="60%">{{ncrValueTableData.nameTitle}}</th>
                <th width="40%">{{ncrValueTableData.valueTitle}}</th>
              </tr>
            </template>
            <template v-slot:body>
              <tr v-for="(dataValue, index) of ncrValueTableData.data" :key="index">
                <td>{{dataValue.name}}</td>
                <td><span v-html="dataValue.displayValue" /></td>
              </tr>
              <tr class="border-top">
                <td><strong>Total</strong></td>
                <td><strong><span v-html="ncrValueTableData.totalDisplayValue" /></strong></td>
              </tr>
            </template>
          </StaticDataTable>
        </Scroller>
      </div>
      <div :class="showList && hasChartData ? 'col-9' : 'col-12'">
        <ChartDoughnut
          :series="ncrValueSeries"
          :legend="ncrValueLegend"
          :options="ncrValueOptions"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>



<script>
import _ from 'lodash'
import moment from 'moment'

import Utils from '@/utils/utils'
import AnalyticsDateMixin from '@/modules/intelligence/mixins/analyticsDate'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportContactSelect from '@/components/reporting/reportContactSelect'
import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import ChartDoughnut from '@/components/charts/chartDoughnut'

import { ChartDateRangeTypes } from '@/modules/intelligence/types'

const NCROrderChartTypes = {
  ncrItemValue: 'ncrItemValue',
  ncrItemCount: 'ncrItemCount',
  ncrOrderBreakdown: 'ncrOrderBreakdown',
  ncrOrderItemBreakdown: 'ncrOrderItemBreakdown'
}

export default {
  name: 'NCROrderChartWidget',
  mixins: [AnalyticsDateMixin],
  components: {
    AnalyticsWidget,
    ChartDoughnut,
    ReportFilterPopover,
    ReportDateRangeSelect,
    ReportContactSelect
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    filter: Object,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean,
    showList: Boolean,
    showDateFilter: Boolean,
    dateRangeType: {
      type: String,
      default: ChartDateRangeTypes.today
    },
    startDate: String,
    endDate: String,
    contactId: Number
  },
  NCROrderChartTypes: NCROrderChartTypes,
  data() {
    return {
      ncrValueSeries: [],
      ncrValueTableData: {},
      ncrValueLegend: [],
      ncrValueOptions: {},
      editingStartDate: this.startDate,
      editingEndDate: this.endDate,
      editingContactId: this.contactId,
      contactList: []
    }
  },
  computed: {
    hasChartData() {
      if(this.ncrValueSeries) {
        return this.ncrValueSeries.some(series => {
          if(series.data && series.data.length > 0)
            return true
        })
      }
      return false
    },
    selectedContact() {
      if(!this.contactId)
        return null

      return this.contactList.find(contact => contact.id == this.contactId)
    }
  },
  mounted() {
    this.requestData()
    this.requestChartData()
  },
  watch: {
    dateRangeType: function() {
      this.requestChartData()
    },
    startDate: function() {
      this.editingStartDate = this.startDate
      this.requestChartData()
    },
    endDate: function() {
      this.editingEndDate = this.endDate
      this.requestChartData()
    },
    contactId: function() {
      this.editingContactId = this.contactId
      this.requestChartData()
    },
    filter: function() {
      this.requestChartData()
    }
  },
  methods: {
    requestData() {
      this.$apiService.contactService.getContactList({ isActive: 1 })
      .then(response => {
        this.contactList = response.data
      })
      .catch(err => {
      })
    },
    requestChartData() {
      var filterData = {
        startDate: this.calculateStartDateForDateRangeType(this.dateRangeType, this.startDate).toDate().toISOString(),
        endDate: this.calculateEndDateForDateRangeType(this.dateRangeType, this.endDate).toDate().toISOString(),
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null,
        contactId: this.contactId
      }

      this.$apiService.analyticsService.getNCRValueSummary(filterData)
      .then(response => {
        var ncrCauseList = response.data.ncrCauseList

        this.ncrValueOptions.tooltip = {
          formatter(param) {
            return `${param.seriesName} <br />${param.name} : ${Utils.formatCurrency(param.value)} (${param.percent}%)`
          }
        }

        if(this.type == NCROrderChartTypes.ncrItemValue) {
          this.ncrValueSeries = [{
            name: 'NCR Cause',
            data: ncrCauseList.map(causeInfo => {
              return {
                name: causeInfo.labelName,
                value: causeInfo.totalItemValue
              }
            }),
            label: {
              show: true,
              formatter(param) {
                return param.name
              }
            },
          }]
          this.ncrValueLegend = {
            data: ncrCauseList.map(causeInfo => causeInfo.labelName)
          }

          this.ncrValueTableData = {
            nameTitle: 'NCR Cause',
            valueTitle: 'Value',
            data: ncrCauseList.map(causeInfo => {
              return {
                name: causeInfo.labelName,
                value: causeInfo.totalItemValue,
                displayValue: Utils.formatCurrency(causeInfo.totalItemValue)
              }
            })
          }

          this.ncrValueTableData.data = _.sortBy(this.ncrValueTableData.data, 'value').reverse()
          this.ncrValueTableData.totalDisplayValue = this.formatCurrency(_.sumBy(this.ncrValueTableData.data, 'value'))
        }

        if(this.type == NCROrderChartTypes.ncrItemCount) {
          this.ncrValueOptions.tooltip = {
            formatter(param) {
              return `${param.seriesName} <br />${param.name} : ${Utils.formatInteger(param.value)} (${param.percent}%)`
            }
          }
          
          this.ncrValueSeries = [{
            name: 'NCR Cause',
            data: ncrCauseList.map(causeInfo => {
              return {
                name: causeInfo.labelName,
                value: causeInfo.itemCount
              }
            }),
            label: {
              show: true,
              formatter(param) {
                return param.name
              }
            },
          }]
          this.ncrValueLegend = {
            data: ncrCauseList.map(causeInfo => causeInfo.labelName)
          }

          this.ncrValueTableData = {
            nameTitle: 'NCR Cause',
            valueTitle: 'Count',
            data: ncrCauseList.map(causeInfo => {
              return {
                name: causeInfo.labelName,
                value: causeInfo.itemCount,
                displayValue: Utils.formatInteger(causeInfo.itemCount)
              }
            })
          }

          this.ncrValueTableData.data = _.sortBy(this.ncrValueTableData.data, 'value').reverse()
          this.ncrValueTableData.totalDisplayValue = _.sumBy(this.ncrValueTableData.data, 'value')
        }


        if(this.type == NCROrderChartTypes.ncrOrderBreakdown) {
          this.ncrValueOptions.tooltip = {
            formatter(param) {
              return `${param.seriesName} <br />${param.name} : ${Utils.formatInteger(param.value)} (${param.percent}%)`
            }
          }
          
          this.ncrValueSeries = [{
            name: 'Breakdown',
            data: [{
              name: 'NCR Orders',
              value: response.data.ncrTotalCount
            }, {
              name: 'Standard Orders',
              value: response.data.orderTotalCount
            }],
            label: {
              show: true,
              formatter(param) {
                return param.name
              }
            },
          }]
          this.ncrValueLegend = {
            data: ['NCR Orders', 'Standard Orders']
          }

          let totalOrders = response.data.ncrTotalCount + response.data.orderTotalCount

          this.ncrValueTableData = {
            nameTitle: 'NCR Breakdown',
            valueTitle: 'Count',
            data: [{
              name: 'NCR Orders',
              value: response.data.ncrTotalCount,
              displayValue: Utils.formatInteger(response.data.ncrTotalCount) + ' (' + Utils.formatPercent((totalOrders > 0 ? (response.data.ncrTotalCount / totalOrders) : 0) * 100.0) + ')'
            }, {
              name: 'Standard Orders',
              value: response.data.orderTotalCount,
              displayValue: Utils.formatInteger(response.data.orderTotalCount) + ' (' + Utils.formatPercent((totalOrders > 0 ? (response.data.orderTotalCount / totalOrders) : 0) * 100.0) + ')'
            }],
          }

          this.ncrValueTableData.data = _.sortBy(this.ncrValueTableData.data, 'value').reverse()
          this.ncrValueTableData.totalDisplayValue = _.sumBy(this.ncrValueTableData.data, 'value')
        }

        if(this.type == NCROrderChartTypes.ncrOrderItemBreakdown) {
          this.ncrValueOptions.tooltip = {
            formatter(param) {
              return `${param.seriesName} <br />${param.name} : ${Utils.formatInteger(param.value)} (${param.percent}%)`
            }
          }
          
          this.ncrValueSeries = [{
            name: 'Item Breakdown',
            data: [{
              name: 'NCR Accepted Quantity',
              value: response.data.ncrItemTotalQuantity
            }, {
              name: 'Standard Order Item Quantity',
              value: response.data.orderItemTotalQuantity
            }],
            label: {
              show: true,
              formatter(param) {
                return param.name
              }
            },
          }]
          this.ncrValueLegend = {
            data: ['NCR Accepted Quantity', 'Standard Order Item Quantity']
          }

          let totalOrderItems = response.data.ncrItemTotalQuantity + response.data.orderItemTotalQuantity

          this.ncrValueTableData = {
            nameTitle: 'NCR Item Breakdown',
            valueTitle: 'Count',
            data: [{
              name: 'NCR Accepted Quantity',
              value: response.data.ncrItemTotalQuantity,
              displayValue: Utils.formatInteger(response.data.ncrItemTotalQuantity) + ' (' + Utils.formatPercent((totalOrderItems > 0 ? (response.data.ncrItemTotalQuantity / totalOrderItems) : 0) * 100.0) + ')'
            }, {
              name: 'Standard Order Item Quantity',
              value: response.data.orderItemTotalQuantity,
              displayValue: Utils.formatInteger(response.data.orderItemTotalQuantity) + ' (' + Utils.formatPercent((totalOrderItems > 0 ? (response.data.orderItemTotalQuantity / totalOrderItems) : 0) * 100.0) + ')'
            }],
          }

          this.ncrValueTableData.data = _.sortBy(this.ncrValueTableData.data, 'value').reverse()
          this.ncrValueTableData.totalDisplayValue = _.sumBy(this.ncrValueTableData.data, 'value')
        }
      })
    }
  }
}


</script>